import React from 'react';
import styles from './Programacion.module.scss';

/* TYPES */

type CountryCode = 'MX' | 'AR';
type ChannelCode = 'MORBIDO' | '4FANGS';

type JsonMatrix = (string | number | null)[];

type TimeSlot = {
  start: string;
  end: string;
  title: string;
  dayOffset: number;
};

type DaySchedule = {
  label: string;
  slots: TimeSlot[];
};

/*  HELPERS */

function detectCountryFromTimeZone(): CountryCode {
  const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
  if (tz.includes('Argentina')) return 'AR';
  return 'MX';
}

function generateTimeRows(): string[] {
  return Array.from({ length: 12 }, (_, i) =>
    `${String(i * 2).padStart(2, '0')}:00`
  );
}

function calcEndHour(start: string): string {
  const h = Number(start.split(':')[0]);
  return `${String((h + 2) % 24).padStart(2, '0')}:00`;
}

function convertHourWithDayOffset(
  hour: string,
  country: CountryCode
): { hour: string; dayOffset: number } {
  const h = Number(hour.split(':')[0]);
  const offset = country === 'AR' ? 3 : 0;
  const raw = h + offset;

  if (raw >= 24) {
    return { hour: `${String(raw - 24).padStart(2, '0')}:00`, dayOffset: 1 };
  }

  return { hour: `${String(raw).padStart(2, '0')}:00`, dayOffset: 0 };
}

function isCurrentSlot(
  dayIndex: number,
  slotStart: string,
  todayIndex: number
): boolean {
  if (dayIndex !== todayIndex) return false;
  const now = new Date().getHours();
  const slot = Number(slotStart.split(':')[0]);
  return now === slot || now === slot + 1;
}

/* 🔹 NUEVO: calcular fechas de la semana (lunes → domingo) */
function getWeekDates(): Date[] {
  const today = new Date();
  const day = today.getDay(); // 0 = domingo
  const diffToMonday = day === 0 ? -6 : 1 - day;

  const monday = new Date(today);
  monday.setDate(today.getDate() + diffToMonday);

  return Array.from({ length: 7 }, (_, i) => {
    const d = new Date(monday);
    d.setDate(monday.getDate() + i);
    return d;
  });
}

/* COMPONENT  */

const Programacion: React.FC = () => {
  const country = detectCountryFromTimeZone();
  const [channel, setChannel] = React.useState<ChannelCode>('MORBIDO');

  const [rawData, setRawData] = React.useState<JsonMatrix[] | null>(null);
  const [loadError, setLoadError] = React.useState<string | null>(null);

  const TIME_ROWS = React.useMemo(() => generateTimeRows(), []);
  const weekDates = React.useMemo(() => getWeekDates(), []);

  /* LOAD JSON  */

  React.useEffect(() => {
    const loadJson = async () => {
      const fileName =
        channel === 'MORBIDO'
          ? '/programacion_json/morbido.json'
          : '/programacion_json/4fangs.json';

      try {
        setLoadError(null);

        const res = await fetch(fileName, { cache: 'no-store' });
        if (!res.ok) throw new Error();

        const json = (await res.json()) as JsonMatrix[];
        setRawData(json);
      } catch {
        setLoadError('Programación no disponible por el momento.');
        setRawData(null);
      }
    };

    loadJson();
  }, [channel]);

  /*  TRANSFORM  */

  const schedule: DaySchedule[] = React.useMemo(() => {
    if (!rawData || rawData.length < 3) return [];

    const headerRow = rawData[0].slice(1, 8); // días
    const rows = rawData.slice(2); // horarios

    return headerRow.map((dayLabel, dayIndex) => ({
      label: String(dayLabel),
      slots: rows.map(row => {
        const startMX = String(row[1] ?? '');
        const { hour, dayOffset } = convertHourWithDayOffset(startMX, country);

        return {
          start: hour,
          end: calcEndHour(hour),
          title: String(row[dayIndex + 2] ?? ''),
          dayOffset,
        };
      }),
    }));
  }, [rawData, country]);

  const todayIndex = React.useMemo(() => {
    const day = new Date().getDay(); // 0 domingo
    return day === 0 ? 6 : day - 1;
  }, []);

  /* RENDER */

  return (
    <div className={styles.container}>
      <h1 className={styles.title}>Programación Semanal</h1>

      <p className={styles.subtitle}>
        Los horarios se muestran en tu hora local
      </p>

      <div className={styles.channelTabs}>
        <button
          className={channel === 'MORBIDO' ? styles.channelTabActive : styles.channelTab}
          onClick={() => setChannel('MORBIDO')}
        >
          Mórbido TV
        </button>
        <button
          className={channel === '4FANGS' ? styles.channelTabActive : styles.channelTab}
          onClick={() => setChannel('4FANGS')}
        >
          4Fangs
        </button>
      </div>

      {loadError && <p>{loadError}</p>}
      {!loadError && schedule.length === 0 && <p>Cargando programación…</p>}

      {schedule.length > 0 && (
        <div className={styles.tableWrapper}>
          <table className={styles.scheduleTable}>
            <thead>
              <tr>
                <th>Hora</th>

                {schedule.map((d, index) => {
                  const date = weekDates[index];
                  const dayName = d.label.toUpperCase();
                  const dateLabel = date.toLocaleDateString('es-MX', {
                    day: 'numeric',
                    month: 'long',
                  });

                  return (
                    <th key={d.label}>
                      <div className={styles.dayHeaderContent}>
                        <span className={styles.dayName}>{dayName}</span>
                        <span className={styles.dayDate}>{dateLabel}</span>
                      </div>
                    </th>
                  );
                })}
              </tr>
            </thead>

            <tbody>
              {TIME_ROWS.map(time => (
                <tr key={time}>
                  <td>{time}</td>
                  {schedule.map((_, dayIndex) => {
                    const slot = schedule
                      .flatMap((day, idx) =>
                        day.slots.map(s => ({
                          ...s,
                          targetDay: idx + s.dayOffset,
                        }))
                      )
                      .find(s => s.start === time && s.targetDay === dayIndex);

                    return (
                      <td
                        key={`${dayIndex}-${time}`}
                        className={
                          slot && isCurrentSlot(dayIndex, slot.start, todayIndex)
                            ? styles.currentSlot
                            : ''
                        }
                      >
                        {slot?.title || ''}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default Programacion;
